import React, { useMemo } from 'react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { DynamicInvitedSignaturesFields } from '@dill/dill-shared/dist/src';
import { DillButton } from '../../../general/Dill/DillButton';

interface InviteField {
  title: string;
  email: string;
  step: number;
  fullNameNeeded?: boolean;
  driverLicenseNeeded?: boolean;
  signatureGroupName?: string;
  placeHolder?: string;
  type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  fields: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  isRequired?: boolean;
  // Required only under a condition that isn't met (the field's conditionalRequired, e.g. the
  // Personal Guarantor unless Business Type is "LLC (Single-member)") — shown without the asterisk.
  isOptional?: boolean;
  formTemplate?: string | null;
  linkedFieldId?: string | null;
  signature?: string;
  dateSigned?: string;
  ipAddress?: string;
  location?: string;
}

interface FieldError {
  placeHolder: string;
  errorText: string;
}

interface InviteSignatureProps {
  data: InviteField[];
  onChange: (updatedData: InviteField[]) => void;
  fieldErrors?: FieldError[];
  value: InviteField[];
  amountOfCredit?: number;
  allowSignNow?: boolean;
  signedNowPlaceholders?: string[];
  senderEmail?: string;
  onSignNow?: (field: InviteField) => void;
  onEditSignNow?: (field: InviteField) => void;
  onClearSignNow?: (field: InviteField) => void;
}

const InviteSignature: React.FC<InviteSignatureProps> = ({
  data,
  value,
  onChange,
  fieldErrors = [],
  amountOfCredit,
  allowSignNow = false,
  signedNowPlaceholders = [],
  senderEmail,
  onSignNow,
  onEditSignNow,
  onClearSignNow,
}) => {
  const handleInputChange = (
    placeHolder: string,
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR',
    val: string
  ) => {
    const updated = [...value];
    const index = updated.findIndex((v) => v.placeHolder === placeHolder && v.type === type);
    if (index !== -1) {
      updated[index].email = val;
    }
    onChange(updated);
  };

  const showMessage = useMemo(() => {
    const minimumNumberOfSignatures = data?.[0]?.minimumNumberOfSignatures;
    if (minimumNumberOfSignatures !== undefined && minimumNumberOfSignatures !== data.length) {
      return 'The minimum number of signatures is ' + minimumNumberOfSignatures;
    }
    return '';
  }, [data]);

  return (
    <div className="border-b pb-4">
      <h3 className="text-sm font-bold mb-1">{data?.[0]?.signatureGroupName ?? ''}</h3>
      {showMessage && data?.[0]?.minimumNumberOfSignatures !== 0 ? (
        <p className="text-xs">
          <span className="text-red-600">*</span>
          {showMessage}
        </p>
      ) : null}
      <div className="space-y-3">
        {data.map((field, index) => {
          const error =
            fieldErrors.find((err) => err.placeHolder === field.placeHolder)?.errorText ?? '';
          const valueField = value.find(
            (v) => v.placeHolder === field.placeHolder && v.type === field.type
          );
          let isRequired = true;
          if (field?.isOptional) {
            isRequired = false;
          } else if (
            field?.minimumCreditAmount !== undefined &&
            amountOfCredit !== undefined &&
            (field.type === 'GUARANTOR' || field.type === 'CORPORATE_GUARANTOR')
          ) {
            isRequired = amountOfCredit > field.minimumCreditAmount;
          } else if (field?.minimumNumberOfSignatures === undefined) {
            isRequired = false;
          }

          const isSignedNow =
            signedNowPlaceholders.includes(field.placeHolder ?? '') || !!valueField?.signature;
          const emailMatchesSender =
            !!senderEmail &&
            !!valueField?.email &&
            valueField.email.trim().toLowerCase() === senderEmail.trim().toLowerCase();
          const firstUnsignedIndex = data.findIndex(
            (f) => !signedNowPlaceholders.includes(f.placeHolder ?? '')
          );
          const sectionHasSignedSlot = data.some((f) =>
            signedNowPlaceholders.includes(f.placeHolder ?? '')
          );
          const showSignNowButton =
            allowSignNow && !isSignedNow && index === firstUnsignedIndex && !sectionHasSignedSlot;
          const filledFields = (valueField?.fields ?? field.fields).filter(
            (f) => f.name && f.value != null && f.value !== ''
          );

          return (
            <div key={`${field.placeHolder}-${index}`} className="mb-1">
              {isSignedNow ? (
                /* Full-width signed card — no outer left label */
                <div className="w-full flex flex-col border rounded-lg overflow-hidden">
                  {/* Actions row */}
                  <div className="flex justify-end gap-2 px-2 pt-2">
                    <DillButton
                      label="Edit"
                      height={24}
                      variant="secondary"
                      onClick={() =>
                        onEditSignNow?.({
                          ...field,
                          fields: valueField?.fields ?? field.fields,
                        })
                      }
                    />
                    <DillButton
                      label="Clear"
                      height={24}
                      variant="secondary"
                      onClick={() => onClearSignNow?.(field)}
                    />
                  </div>

                  {/* Title + signature details */}
                  <div className="grid grid-cols-2 gap-1 md:gap-2">
                    <div className="flex flex-col items-start gap-1 p-2">
                      <div className="text-xs font-semibold">
                        {field?.placeHolder ?? ''}
                        {showMessage === '' && isRequired && (
                          <span className="text-red-500">*</span>
                        )}
                      </div>
                      <div className="text-xs">{valueField?.email ?? ''}</div>
                    </div>
                    <div className="flex flex-col items-start gap-1 p-2">
                      {valueField?.signature && (
                        <div className="flex items-center gap-2">
                          <img
                            src={valueField.signature}
                            alt="Signature"
                            className="h-[24px] w-auto object-contain"
                          />
                          <p className="text-[9px] text-gray-500">E-signed using Dill</p>
                        </div>
                      )}
                      {(valueField?.dateSigned ||
                        valueField?.ipAddress ||
                        valueField?.location) && (
                        <div className="text-[10px] text-gray-500">
                          {valueField?.dateSigned && `Date Signed: ${valueField.dateSigned}`}
                          {valueField?.location && ` at ${valueField.location}`}
                          {valueField?.ipAddress && ` from IP Address ${valueField.ipAddress}`}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dynamic fields */}
                  {filledFields.length > 0 && (
                    <div className="grid grid-cols-2 gap-1 md:gap-2 border-t">
                      {filledFields.map((f) => (
                        <div key={f.id} className="flex flex-col items-start gap-1 p-2">
                          <div className="text-xs font-semibold">
                            {f.name}
                            {f.isRequired && <span className="text-red-500">*</span>}
                          </div>
                          <div className="text-xs">
                            {f.value instanceof File
                              ? f.value.name
                              : typeof f.value === 'object' && f.value !== null
                              ? (f.value as { name?: string })?.name ?? ''
                              : String(f.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Unsigned — original two-column layout */
                <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
                  <div className="w-full text-sm pt-1">
                    {field?.placeHolder ?? ''}
                    {showMessage === '' && isRequired && <span className="text-red-600">*</span>}
                  </div>
                  <div className="w-full mb-1 flex flex-col gap-1.5">
                    {showSignNowButton && (
                      <DillButton
                        label="Sign Now"
                        height={35}
                        variant="primary"
                        onClick={() => onSignNow?.(field)}
                      />
                    )}
                    {showSignNowButton && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400">or enter email to invite</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    )}
                    <AppInputField
                      placeholder="Enter Email"
                      label={showSignNowButton ? undefined : `Enter the signer's email address`}
                      autoComplete="new-password"
                      value={valueField?.email ?? ''}
                      errorText={
                        error
                          ? error
                          : emailMatchesSender && !isSignedNow
                          ? 'This is your email — please use "Sign Now" to sign this slot.'
                          : undefined
                      }
                      onTextChange={(text) =>
                        handleInputChange(field?.placeHolder ?? '', field.type, text)
                      }
                      onBlur={() => {
                        handleInputChange(
                          field?.placeHolder ?? '',
                          field.type,
                          `${valueField?.email ?? ''}`.trim().toLowerCase()
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InviteSignature;
